import { FunctionComponent, Fragment } from 'preact';
import { useMemo } from 'preact/hooks';
import { formatPublishedDate, formatDuration } from '@vgtv/time-helpers';
import type { Asset } from '@schibsted-svp/svp-api-types';

import { Typography } from '../Typography/Typography';
import { getRealAssetFlightTimesStart } from '../../utils/assetHelpers';
import { type i18n, useTranslation } from '../../../../utils/useTranslation';
import ns from '../../translations';
import { getAssetLabel } from '../../utils/assetLabel';
import { AgeLimitLabel } from '../AgeLimitLabel/AgeLimitLabel';
import { getAssetAgeLimit } from '../../utils/ageLimit';
import { getSubscriptionLabel } from '../../../../utils/subscription';
import { useAccessDefinitions } from '../../hooks/AccessDefinitionsProvider';
import { getAssetAccess } from '../../../../utils/player/asset';
import { Tag } from '../Tag/Tag';

export interface RichAssetTitleProps {
  asset: Asset;
  titleOverride?: string;
}

export const getDurationLabel = (asset: Asset) => {
  const durationInMilliseconds = asset.duration;

  if (!durationInMilliseconds) {
    return undefined;
  }

  return formatDuration(durationInMilliseconds);
};

export const getTimestampLabel = (asset: Asset, i18n: i18n) => {
  const flightTimesStartInSeconds = getRealAssetFlightTimesStart(asset, false);

  if (!flightTimesStartInSeconds) {
    return undefined;
  }

  return formatPublishedDate(
    flightTimesStartInSeconds,
    i18n.locale === 'no' ? 'nb' : 'sv'
  );
};

export const RichAssetTitle: FunctionComponent<RichAssetTitleProps> = ({
  asset,
  titleOverride,
}) => {
  const { i18n } = useTranslation(ns);
  const title = titleOverride || asset.title;

  const duration = useMemo(() => getDurationLabel(asset), [asset]);
  const timestamp = useMemo(
    () => getTimestampLabel(asset, i18n),
    [asset, i18n]
  );

  const assetLabel = getAssetLabel(asset);

  const { accessDefinitions } = useAccessDefinitions();
  const accessLevel = getAssetAccess(asset);

  const labels = useMemo(() => {
    const ageLimit = getAssetAgeLimit(asset);
    const subscriptionLabel = getSubscriptionLabel(
      accessDefinitions,
      accessLevel
    );
    return [
      duration && <Tag>{duration}</Tag>,
      subscriptionLabel && <Tag>{subscriptionLabel}</Tag>,
      ageLimit !== null && <AgeLimitLabel ageLimit={ageLimit} />,
    ].filter(Boolean);
  }, [asset, assetLabel, timestamp, duration, accessDefinitions]);

  return (
    <div>
      <Typography
        title={title}
        variant="body"
        size="medium"
        color="subtitle-text"
        bold
      >
        <span className="vgp-title-main">{title}</span>
      </Typography>
      <Typography variant="label" size="medium" className="vgp-title-meta">
        <span className="vgp-title-meta-container">{labels}</span>
      </Typography>
    </div>
  );
};
